import './PwaUpdateBanner.css';
import { useEffect, useRef, useState } from 'react';
import { RefreshCw } from 'lucide-react';

/**
 * Shows a "new version available" banner instead of relying on a master to
 * notice a stale PWA install and manually close/reopen it. `index.js` wires
 * `serviceWorkerRegistration`'s `onUpdate` callback to dispatch
 * `sw-update-available` on `window` once a new service worker has finished
 * installing (still in `waiting` state, not yet controlling the page) — this
 * component just listens for that and offers a one-tap reload.
 */
function PwaUpdateBanner() {
    const [registration, setRegistration] = useState(null);
    const [reloading, setReloading] = useState(false);
    const reloadedRef = useRef(false);

    useEffect(() => {
        const handleUpdate = (event) => setRegistration(event.detail);
        window.addEventListener('sw-update-available', handleUpdate);
        return () => window.removeEventListener('sw-update-available', handleUpdate);
    }, []);

    useEffect(() => {
        if (!('serviceWorker' in navigator)) return;
        const handleControllerChange = () => {
            if (reloadedRef.current) return;
            reloadedRef.current = true;
            window.location.reload();
        };
        navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
        return () => navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
    }, []);

    const handleUpdateClick = () => {
        if (!registration?.waiting) return;
        setReloading(true);
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
        // Fallback in case controllerchange never fires (e.g. only one client open).
        setTimeout(() => {
            if (!reloadedRef.current) {
                reloadedRef.current = true;
                window.location.reload();
            }
        }, 2000);
    };

    if (!registration?.waiting) return null;

    return (
        <div className="pwa-update-banner" role="status">
            <RefreshCw size={16} className="pwa-update-banner__icon" />
            <span className="pwa-update-banner__text">Доступна нова версія додатку.</span>
            <button className="pwa-update-banner__btn" onClick={handleUpdateClick} disabled={reloading}>
                {reloading ? 'Оновлення…' : 'Оновити'}
            </button>
        </div>
    );
}

export default PwaUpdateBanner;
